import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowLeft, Play, ExternalLink, X, ArrowUp, MessageSquare } from 'lucide-react';
interface FeedContentItem {
  id: string;
  source: 'YouTube' | 'Reddit';
  title: string;
  creator: string;
  subtitle?: string;
  thumbnail: string;
  time: string;
  stats?: {
    label: string;
    value: string;
  }[];
  tags?: string[];
  channel?: string;
  subreddit?: string;
  duration?: string;
}
const CONTENT: FeedContentItem[] = [{
  id: '1',
  source: 'YouTube',
  title: 'Building voice AI agents with LiveKit',
  creator: 'LiveKit',
  thumbnail: '\ud83c\udfa5',
  time: '2 days ago',
  duration: '24:18',
  channel: 'LiveKit',
  tags: ['AI', 'Engineering'],
  stats: [{
    label: 'views',
    value: '42K'
  }, {
    label: 'likes',
    value: '1.8K'
  }],
  subtitle: 'A walkthrough of building production voice AI agents using the LiveKit Agents SDK'
}, {
  id: '2',
  source: 'YouTube',
  title: 'Claude 4.5 — what\'s new and how to use it',
  creator: 'Anthropic',
  thumbnail: '\ud83e\udd16',
  time: '5 days ago',
  duration: '12:45',
  channel: 'Anthropic',
  tags: ['AI', 'Claude'],
  stats: [{
    label: 'views',
    value: '128K'
  }, {
    label: 'likes',
    value: '5.2K'
  }],
  subtitle: 'Overview of the new features in Claude 4.5 including extended thinking and tool use improvements'
}, {
  id: '3',
  source: 'Reddit',
  title: 'Anyone using Hetzner for AI workloads? Looking for GPU pricing comparison',
  creator: 'u/matt_dev',
  thumbnail: '\ud83d\udcac',
  time: '3h ago',
  subreddit: 'r/selfhosted',
  tags: ['Discussion', 'Infrastructure'],
  stats: [{
    label: 'upvotes',
    value: '234'
  }, {
    label: 'comments',
    value: '56'
  }],
  subtitle: 'We\'re running a few AI workloads and evaluating providers. Hetzner came up as a strong candidate for CPU-heavy work but I\'m curious about GPU options...'
}, {
  id: '4',
  source: 'Reddit',
  title: 'Nango vs n8n for integration pipelines?',
  creator: 'u/data_pipe',
  thumbnail: '\ud83d\udd0c',
  time: '8h ago',
  subreddit: 'r/dataengineering',
  tags: ['Discussion'],
  stats: [{
    label: 'upvotes',
    value: '89'
  }, {
    label: 'comments',
    value: '34'
  }],
  subtitle: 'Has anyone built a comparison between Nango and n8n for syncing data from SaaS tools? Looking for pros/cons from real-world usage...'
}, {
  id: '5',
  source: 'YouTube',
  title: 'Convex deep dive \u2014 self-hosted real-time database',
  creator: 'Fireship',
  thumbnail: '\ud83d\udd25',
  time: '1 week ago',
  duration: '8:32',
  channel: 'Fireship',
  tags: ['Engineering', 'Database'],
  stats: [{
    label: 'views',
    value: '245K'
  }, {
    label: 'likes',
    value: '12K'
  }],
  subtitle: 'Quick look at Convex \u2014 the reactive database that powers real-time apps with TypeScript-first queries'
}, {
  id: '6',
  source: 'Reddit',
  title: 'Best practices for dark mode in modern web apps?',
  creator: 'u/design_nerd',
  thumbnail: '\ud83c\udf19',
  time: '12h ago',
  subreddit: 'r/webdev',
  tags: ['Design'],
  stats: [{
    label: 'upvotes',
    value: '567'
  }, {
    label: 'comments',
    value: '127'
  }],
  subtitle: 'Working on a complete redesign and going dark-first. What are the current best practices for contrast, colour palettes, and accessibility?'
}, {
  id: '7',
  source: 'YouTube',
  title: 'Zen minimalism in UI design \u2014 from Muji to Apple',
  creator: 'Design Theory',
  thumbnail: '\u2728',
  time: '3 weeks ago',
  duration: '18:22',
  channel: 'Design Theory',
  tags: ['Design', 'Philosophy'],
  stats: [{
    label: 'views',
    value: '89K'
  }, {
    label: 'likes',
    value: '4.1K'
  }],
  subtitle: 'Exploring how Japanese zen principles influenced modern product design from Muji\'s household items to Apple\'s interfaces'
}, {
  id: '8',
  source: 'Reddit',
  title: 'Show HN: I built a voice-first AI assistant for solo founders',
  creator: 'u/founder_tools',
  thumbnail: '\ud83c\udf99\ufe0f',
  time: 'yesterday',
  subreddit: 'r/startups',
  tags: ['Show HN'],
  stats: [{
    label: 'upvotes',
    value: '1.2k'
  }, {
    label: 'comments',
    value: '203'
  }],
  subtitle: 'After 6 months of building, excited to share Zenbot \u2014 a voice-first AI assistant focused on helping solo founders stay in flow...'
}];
const TABS = ['All', 'YouTube', 'Reddit'] as const;
const YT_FILTERS: Record<string, string[]> = {
  Channel: ['LiveKit', 'Anthropic', 'Fireship', 'Design Theory'],
  Tags: ['AI', 'Engineering', 'Design', 'Database', 'Philosophy', 'Claude'],
  Duration: ['< 10 min', '10-30 min', '> 30 min']
};
const REDDIT_FILTERS: Record<string, string[]> = {
  Subreddit: ['r/selfhosted', 'r/dataengineering', 'r/webdev', 'r/startups', 'r/programming'],
  Tags: ['Discussion', 'Show HN', 'Infrastructure', 'Design'],
  Top: ['Today', 'This week', 'This month']
};
type FilterState = Record<string, string[]>;
function FilterPills({
  filters,
  activeFilters,
  onToggle
}: {
  filters: Record<string, string[]>;
  activeFilters: FilterState;
  onToggle: (c: string, v: string) => void;
}) {
  return <div className="flex flex-wrap gap-x-6 gap-y-3 mb-6" data-id="element-964">
      {Object.entries(filters).map(([cat, vals]) => <div key={cat} className="flex items-center gap-2 flex-wrap" data-id="element-965">
          <span className="text-[12px] text-[#8a8680]/50 uppercase tracking-wider" data-id="element-966">{cat}</span>
          {vals.map(v => {
        const a = (activeFilters[cat] || []).includes(v);
        return <button key={v} onClick={() => onToggle(cat, v)} className={`text-[13px] px-2.5 py-1 rounded-full transition-colors ${a ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680] hover:text-[#e8e4df]'}`} data-id="element-967">{v}{a && <X size={10} className="inline ml-1.5 -mt-0.5" data-id="element-968" />}</button>;
      })}
        </div>)}
    </div>;
}
export function FeedContentView() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string>('All');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<FilterState>({});
  const toggleFilter = (c: string, v: string) => {
    setActiveFilters(prev => {
      const cur = prev[c] || [];
      return {
        ...prev,
        [c]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]
      };
    });
  };
  if (viewingId) {
    const item = CONTENT.find(c => c.id === viewingId);
    if (item) return <ContentDetail item={item} onBack={() => setViewingId(null)} data-id="element-969" />;
  }
  const currentFilters = activeTab === 'YouTube' ? YT_FILTERS : activeTab === 'Reddit' ? REDDIT_FILTERS : null;
  const hasActive = Object.values(activeFilters).some(v => v.length > 0);
  const filtered = CONTENT.filter(c => {
    if (activeTab !== 'All' && c.source !== activeTab) return false;
    if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !(c.subtitle || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (!hasActive) return true;
    for (const [cat, vals] of Object.entries(activeFilters)) {
      if (vals.length === 0) continue;
      if (cat === 'Channel' && c.channel && !vals.includes(c.channel)) return false;
      if (cat === 'Subreddit' && c.subreddit && !vals.includes(c.subreddit)) return false;
      if (cat === 'Tags' && c.tags && !c.tags.some(t => vals.includes(t))) return false;
    }
    return true;
  });
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-970">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-971">
        <header className="mb-6" data-id="element-972"><h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-973">Feed</h1><p className="text-[#8a8680] text-[14px]" data-id="element-974">Content from your subscriptions and follows.</p></header>
        <div className="relative mb-6" data-id="element-975"><Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8680]/50" data-id="element-976" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search feed..." className="w-full bg-[#1c1c22] text-[#e8e4df] rounded-[12px] pl-11 pr-4 py-3 text-[15px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30" data-id="element-977" /></div>
        <nav className="flex items-center gap-6 mb-5 border-b border-[#8a8680]/10" data-id="element-978">
          {TABS.map(tab => {
          const isActive = activeTab === tab;
          return <button key={tab} onClick={() => {
            setActiveTab(tab);
            setActiveFilters({});
          }} className={`pb-3 text-[14px] font-medium transition-colors relative ${isActive ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-979">{tab}{isActive && <motion.div layoutId="feedContentTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-980" />}</button>;
        })}
        </nav>
        {currentFilters && <FilterPills filters={currentFilters} activeFilters={activeFilters} onToggle={toggleFilter} data-id="element-981" />}
        <div className="flex flex-col gap-3" data-id="element-982">
          {filtered.map((item, i) => <motion.button key={item.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: i * 0.04
        }} onClick={() => setViewingId(item.id)} className="bg-[#1c1c22] rounded-[16px] p-5 shadow-xl shadow-black/20 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-983">
              <div className="flex items-start gap-4" data-id="element-984">
                <div className="w-16 h-16 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0 text-2xl relative" data-id="element-985">
                  <span data-id="element-986">{item.thumbnail}</span>
                  {item.source === 'YouTube' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-987"><Play size={20} className="text-[#e8e4df]/80 fill-[#e8e4df]/80" data-id="element-988" /></div>}
                </div>
                <div className="flex-1 min-w-0" data-id="element-989">
                  <div className="flex items-center gap-2 mb-1" data-id="element-990">
                    <span className="text-[11px] text-[#8a8680]/50 bg-[#252530] px-2 py-0.5 rounded-full" data-id="element-991">{item.source}</span>
                    {item.channel && <span className="text-[12px] text-[#8a8680]/60" data-id="element-992">{item.channel}</span>}
                    {item.subreddit && <span className="text-[12px] text-[#8a8680]/60" data-id="element-993">{item.subreddit}</span>}
                    {item.duration && <span className="text-[11px] text-[#8a8680]/40" data-id="element-994">{item.duration}</span>}
                  </div>
                  <h2 className="text-[#e8e4df] font-medium text-[15px] leading-[1.4] line-clamp-2" data-id="element-995">{item.title}</h2>
                  {item.subtitle && <p className="text-[#8a8680] text-[13px] mt-1 line-clamp-1" data-id="element-996">{item.subtitle}</p>}
                  <div className="flex items-center gap-3 mt-2" data-id="element-997">
                    {item.stats && item.stats.map((s, idx) => <span key={idx} className="flex items-center gap-1 text-[11px] text-[#8a8680]/40" data-id="element-998">
                        {s.label === 'upvotes' && <ArrowUp size={10} data-id="element-999" />}
                        {s.label === 'comments' && <MessageSquare size={10} data-id="element-1000" />}
                        {s.value} {s.label}
                      </span>)}
                  </div>
                </div>
                <span className="text-[12px] text-[#8a8680]/40 whitespace-nowrap shrink-0" data-id="element-1001">{item.time}</span>
              </div>
            </motion.button>)}
          {filtered.length === 0 && <p className="text-[#8a8680]/50 text-sm text-center py-12" data-id="element-1002">No content found.</p>}
        </div>
      </div>
    </div>;
}
function ContentDetail({
  item,
  onBack
}: {
  item: FeedContentItem;
  onBack: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1003">
      <div className="max-w-[680px] mx-auto w-full" data-id="element-1004">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors mb-8" data-id="element-1005"><ArrowLeft size={16} data-id="element-1006" /><span className="text-sm" data-id="element-1007">Feed</span></button>
        <header className="mb-6" data-id="element-1008">
          <div className="flex items-center gap-2 mb-3" data-id="element-1009">
            <span className="text-[11px] text-[#8a8680]/50 bg-[#252530] px-2 py-0.5 rounded-full" data-id="element-1010">{item.source}</span>
            {item.channel && <span className="text-[12px] text-[#8a8680]/60" data-id="element-1011">{item.channel}</span>}
            {item.subreddit && <span className="text-[12px] text-[#8a8680]/60" data-id="element-1012">{item.subreddit}</span>}
            <span className="text-[#8a8680]/20" data-id="element-1013">&middot;</span>
            <span className="text-[13px] text-[#8a8680]/50" data-id="element-1014">{item.time}</span>
          </div>
          <h1 className="text-[22px] font-bold text-[#e8e4df] tracking-tight leading-[1.3]" data-id="element-1015">{item.title}</h1>
          <p className="text-[13px] text-[#8a8680]/60 mt-2" data-id="element-1016">by {item.creator}</p>
        </header>

        {/* Mock player/preview */}
        <div className="bg-[#1c1c22] rounded-[16px] aspect-video flex items-center justify-center shadow-xl shadow-black/20 mb-6 relative overflow-hidden" data-id="element-1017">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4a9a9a]/5 to-transparent" data-id="element-1018" />
          <div className="text-7xl relative z-10" data-id="element-1019">{item.thumbnail}</div>
          {item.source === 'YouTube' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-1020">
              <div className="w-16 h-16 rounded-full bg-[#4a9a9a]/20 flex items-center justify-center backdrop-blur-sm" data-id="element-1021">
                <Play size={28} className="text-[#e8e4df] fill-[#e8e4df] ml-1" data-id="element-1022" />
              </div>
            </div>}
          {item.duration && <span className="absolute bottom-3 right-3 text-[12px] bg-black/60 text-[#e8e4df] px-2 py-0.5 rounded" data-id="element-1023">{item.duration}</span>}
        </div>

        {item.subtitle && <div className="bg-[#1c1c22] rounded-[16px] p-6 shadow-xl shadow-black/20 mb-6" data-id="element-1024">
            <p className="text-[#e8e4df]/80 text-[15px] leading-[1.8]" data-id="element-1025">{item.subtitle}</p>
          </div>}

        {/* Stats */}
        {item.stats && <div className="flex items-center gap-6 mb-6" data-id="element-1026">
            {item.stats.map((s, i) => <div key={i} className="flex items-center gap-2" data-id="element-1027">
                {s.label === 'upvotes' && <ArrowUp size={14} className="text-[#8a8680]/60" data-id="element-1028" />}
                {s.label === 'comments' && <MessageSquare size={14} className="text-[#8a8680]/60" data-id="element-1029" />}
                <span className="text-[14px] text-[#e8e4df]" data-id="element-1030"><span className="font-medium" data-id="element-1031">{s.value}</span> <span className="text-[#8a8680]" data-id="element-1032">{s.label}</span></span>
              </div>)}
          </div>}

        {item.tags && item.tags.length > 0 && <div className="flex items-center gap-2 mb-6" data-id="element-1033">
            {item.tags.map(t => <span key={t} className="text-[11px] text-[#8a8680]/50 bg-[#252530] px-2 py-0.5 rounded-full" data-id="element-1034">{t}</span>)}
          </div>}

        <button className="flex items-center gap-1.5 text-[#8a8680]/40 hover:text-[#4a9a9a] transition-colors text-[13px]" data-id="element-1035"><ExternalLink size={14} data-id="element-1036" />Open in {item.source}</button>
      </div>
    </motion.div>;
}